import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mic, Volume2, Languages, Loader2, Play } from "lucide-react";
import { Header } from "@/pages/Operations";
import { useT } from "@/lib/i18n";
import api, { mediaUrl } from "@/lib/api";

const VOICES = ["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"];
const LANGS = ["Hindi", "Spanish", "French", "German", "Japanese", "Arabic", "Portuguese", "Mandarin", "Italian"];

export default function VoiceDubbing() {
  const { t } = useT();
  const [tab, setTab] = useState("tts");
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("onyx");
  const [lang, setLang] = useState("Hindi");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generateTTS = async () => {
    if (!text.trim()) { toast.error(t("Enter some text")); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/voice/tts", { text, voice });
      setResult({ kind: "tts", url: r.data.url });
      toast.success(t("Speech synthesized"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Synthesis failed"); }
    finally { setBusy(false); }
  };

  const generateDub = async () => {
    if (!text.trim()) { toast.error(t("Enter script to dub")); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/voice/dub", { text, target_language: lang, voice });
      setResult({ kind: "dub", url: r.data.url, translated: r.data.translated_text, lang });
      toast.success(`${t("Dub into")} ${t(lang)} ✓`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Dubbing failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="voice-dubbing-page" className="p-5 sm:p-8 max-w-[1000px] mx-auto">
      <Header icon={Mic} eyebrow={t("Voice & Dubbing Lab · HD Neural")} title={t("Advanced Voice Lab")}
        subtitle={t("Real high-fidelity text-to-speech and one-click auto-dubbing across languages.")} />

      <div className="flex gap-2 mb-6">
        {[["tts", "Text-to-Speech", Volume2], ["dub", "Auto-Dubbing", Languages]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`voice-tab-${id}`} onClick={() => { setTab(id); setResult(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${
              tab === id ? "bg-gradient-to-r from-purple-600/25 to-cyan-500/15 border-purple-500/40 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>
            <Icon size={15} /> {t(label)}
          </button>
        ))}
      </div>

      <div className="glass rounded-2xl p-6 space-y-4">
        <textarea data-testid="voice-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={5}
          placeholder={t(tab === "tts" ? "Type text to convert to natural speech..." : "Enter the script to translate and dub...")}
          className="w-full bg-[#0A0C14]/60 border border-white/10 rounded-xl p-3.5 text-sm outline-none focus:border-purple-500/40 resize-none placeholder:text-slate-600" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">{t("Voice")}</label>
            <select data-testid="voice-select" value={voice} onChange={(e) => setVoice(e.target.value)}
              className="mt-1 w-full bg-[#0A0C14]/60 border border-white/10 rounded-xl p-2.5 text-sm outline-none focus:border-purple-500/40">
              {VOICES.map((v) => <option key={v} className="bg-[#0E1019]">{v}</option>)}
            </select>
          </div>
          {tab === "dub" && (
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">{t("Target language")}</label>
              <select data-testid="lang-select" value={lang} onChange={(e) => setLang(e.target.value)}
                className="mt-1 w-full bg-[#0A0C14]/60 border border-white/10 rounded-xl p-2.5 text-sm outline-none focus:border-purple-500/40">
                {LANGS.map((l) => <option key={l} value={l} className="bg-[#0E1019]">{t(l)}</option>)}
              </select>
            </div>
          )}
        </div>

        <button data-testid="voice-generate-button" onClick={tab === "tts" ? generateTTS : generateDub} disabled={busy}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-600 text-white font-semibold pulse-glow hover:scale-[1.01] transition-transform disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />}
          {busy ? t("Generating...") : tab === "tts" ? t("Synthesize Speech") : `${t("Dub into")} ${t(lang)}`}
        </button>

        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="pt-2 space-y-3">
            {result.kind === "dub" && (
              <div className="p-3 rounded-xl border border-white/10 bg-white/5">
                <div className="text-[10px] font-mono uppercase text-slate-500 mb-1">{t(result.lang)} {t("translation")}</div>
                <div className="text-sm">{result.translated}</div>
              </div>
            )}
            <audio data-testid="voice-audio-player" controls autoPlay src={mediaUrl(result.url)} className="w-full" />
          </motion.div>
        )}
      </div>
    </div>
  );
}
